import { getAuth } from "firebase/auth";
import { logDebug, logError } from "../shared/common/logger";
import { EstimaticsFileTypes, EstimaticsProjectStoreType, getEstimaticsCurrentProjectStore, getUserStore } from "../data/DataStores";
import { v4 as uuidv4 } from "uuid";
import { appFeatures } from "../shared/common/AppFeatures";
import Analytics from "./Analytics";

class RequestQueue {
	private queue: (() => Promise<any>)[] = [];
	private isProcessing = false;

	async addToQueue(request: () => Promise<any>): Promise<any> {
		return new Promise((resolve, reject) => {
			this.queue.push(async () => {
				try {
					const result = await request();
					resolve(result);
				} catch (error) {
					reject(error);
				}
			});
			this.processRequestQueue();
		});
	}

	private async processRequestQueue() {
		if (this.isProcessing || this.queue.length === 0) {
			return;
		}

		this.isProcessing = true;
		const nextRequest = this.queue.shift();
		if (nextRequest) {
			await nextRequest();
		}
		this.isProcessing = false;
		this.processRequestQueue();
	}
}

export interface UploadProgressCallback {
	(progress: number): void;
}
export default class RestAPI {
	private static _requestQueue = new RequestQueue();
	public static DEV_SERVER_BASE_URL = "http://localhost:8080";
	public static PROD_SERVER_BASE_URL = `https://${location.host}`;

	public static get PUBLIC_BASE_URL() {
		return `${RestAPI.SERVER_BASE_URL}/output`;
	}
	public static get API_URL() {
		return `${RestAPI.SERVER_BASE_URL}/rest/v1`;
	}

	public static get SERVER_BASE_URL() {
		if (appFeatures.overrideServerBaseURL) {
			return appFeatures.overrideServerBaseURL;
		}
		return process.env.NODE_ENV !== "production" ? RestAPI.DEV_SERVER_BASE_URL : RestAPI.PROD_SERVER_BASE_URL;
	}

	private static _lassoToken: string | null = null;
	public static getServerBaseUrl(path: string): string {
		if (path.startsWith("/")) {
			path = path.substring(1);
		}
		if (path.startsWith("home/lasso/output")) {
			return `${RestAPI.PROD_SERVER_BASE_URL}/${path}`;
		}
		return `${RestAPI.SERVER_BASE_URL}/${path}`;
	}

	public static getStorageFileUrl(path: string): string {
		if (path.startsWith("http")) {
			return path;
		}
		if (path.startsWith("/")) {
			path = path.substring(1);
		}

		let ret = `${RestAPI.SERVER_BASE_URL}/rest/files/${path}`;
		if (ret.includes("?")) {
			ret += "&lasso-token=" + RestAPI._lassoToken;
		} else {
			ret += "?lasso-token=" + RestAPI._lassoToken;
		}
		return ret;
	}

	static async fetchAPI(api: string, method = "GET", params: any = {}, dataField = "data", sendAuth = true): Promise<any> {
		if (Object.keys(params).length > 0 && method === "GET") {
			api += "?";
			Object.entries(params).forEach(([key, value]) => {
				api += `${key}=${value}&`;
			});
			params = {};
		}
		const newURL = api.startsWith("/rest/") ? `${RestAPI.SERVER_BASE_URL}${api}` : `${RestAPI.API_URL}/${api}`;
		const retData = await this.fetchURL(newURL, method, params, dataField, sendAuth);
		// logDebug('fetchAPI', api, method, params, 'response:', retData);
		return retData;
	}

	static async getIdToken(): Promise<string | null> {
		if (getAuth().currentUser) {
			try {
				return await getAuth().currentUser!.getIdToken();
			} catch (err) {
				logError("Error in getIdToken :", err);
			}
		}
		return null;
	}

	static async getBearerHeaders(): Promise<{ [s: string]: string }> {
		const authToken = (await this.getIdToken()) || "";
		return {
			authorization: "Bearer " + authToken,
			uid: getAuth().currentUser!.uid,
			"lasso-token": RestAPI._lassoToken || "",
		};
	}

	private static _useQueue = false;
	static async fetchURL(url: URL | string, method = "GET", params: any = {}, dataField = "data", sendAuth = true): Promise<any> {
		const request = async () => {
			const isFormData = params instanceof FormData;
			if (typeof url === "string") {
				url = new URL(url);
			}
			let authToken = "none";
			let uid = "none";
			let response;
			if (sendAuth) {
				const auth = getAuth();
				authToken = (await this.getIdToken()) || "";
				uid = auth.currentUser?.uid ?? "none";
			}
			// logDebug('using lasso token', RestAPI._lassoToken)
			const methodsWithBody = ["POST", "PUT", "PATCH"];
			if (methodsWithBody.includes(method)) {
				response = await fetch(url, {
					method,
					body: isFormData ? params : JSON.stringify(params),
					headers: isFormData
						? {
							authorization: "Bearer " + authToken,
							uid: uid,
							"lasso-token": RestAPI._lassoToken || "",
						}
						: {
							"Content-Type": "application/json;charset=UTF-8",
							Accept: "application/json",
							authorization: "Bearer " + authToken,
							uid: uid,
							"lasso-token": RestAPI._lassoToken || "",
						},
				});
			} else {
				try {
					const headers = {
						"Content-Type": "application/json;charset=UTF-8",
						Accept: "application/json",
						authorization: "Bearer " + authToken,
						uid: uid,
						"lasso-token": RestAPI._lassoToken || "",
					};
					// logDebug('sending headers', headers);
					response = await fetch(url, {
						method,
						headers,
					});
				} catch (error) {
					return null;
				}
			}
			try {
				// TODO :: check success flag in response
				const data: any = await response.json();
				if (!data.success) {
					logError("Error in fetchURL :", url.href, params, data);
					return null;
				}
				if (data.lassoToken) {
					logDebug("Lasso token updated", data.lassoToken);
					// set local cookie
					document.cookie = `lassoToken=${data.lassoToken};max-age=3600;SameSite=Strict`;
					RestAPI._lassoToken = data.lassoToken;
					// logDebug('Lasso token updated ' + RestAPI._lassoToken);
				}
				if (dataField && dataField in data) {
					return data[dataField];
				}
				return data;
			} catch (error) {
				try {
					const text: any = await response.text();
					return text;
				} catch (err) {
					logError("Error reading text from url :", err);
				}
			}
			return null;
		};
		if (RestAPI._useQueue) {
			return RestAPI._requestQueue.addToQueue(request);
		} else {
			return request();
		}
	}

	// TODO :: These upload function not really belong here, move them to a separate file
	static async uploadFileWithProgress(
		file: File,
		uploadId: string,
		destination: string,
		estimateData: {
			estimateId: string;
			tpa: string;
			carrier: string;
		},
		postUploadCommand: string,
		onProgress: UploadProgressCallback
	): Promise<{ postUploadResults: any[]; source: string; destination: string; uploadId: string; mime: string }[] | null> {
		try {
			const xhr = new XMLHttpRequest();
			const formData = new FormData();
			formData.append("files", file);
			formData.append("destination", destination);
			formData.append("estimateId", estimateData.estimateId);
			formData.append("tpa", estimateData.tpa);
			formData.append("carrier", estimateData.carrier);
			formData.append("postUploadCommand", postUploadCommand);
			formData.append("uploadId", uploadId);

			const headers = await RestAPI.getBearerHeaders();
			xhr.open("POST", RestAPI.getServerBaseUrl("/rest/files/upload")); // Replace with your upload URL

			for (const [key, value] of Object.entries(headers)) {
				xhr.setRequestHeader(key, value);
			}

			// Progress event
			xhr.upload.onprogress = (event: ProgressEvent) => {
				if (event.lengthComputable) {
					const percentComplete = (event.loaded / event.total) * 100;
					// logDebug(`Upload progress: ${percentComplete.toFixed(2)}%`);
					onProgress(percentComplete);
				}
			};

			const ret = await new Promise((resolve, reject) => {
				// Load event
				xhr.onload = () => {
					if (xhr.status === 200) {
						resolve(xhr.response);
					} else {
						reject(new Error(`Upload failed: ${xhr.statusText}`));
					}
				};

				// Error event
				xhr.onerror = () => {
					reject(new Error("Upload failed"));
				};

				if (file.name.includes("!$$$$$SimulateError$$$$$!")) {
					onProgress(0);
					setTimeout(() => {
						onProgress(10);
					}, 500);
					setTimeout(() => {
						onProgress(20);
					}, 1000);
					setTimeout(() => {
						reject(new Error("Simulated error"));
					}, 1500);
				} else {
					// Send the request
					xhr.send(formData);
					onProgress(0);
				}
			});
			const retJson = JSON.parse(ret as string) as {
				success: boolean;
				data: { postUploadResults: any[]; originalFileName: string; storagePath: string; mime: string }[];
			};
			if (retJson.success) {
				logDebug("Upload success", retJson.data);
				return [
					{
						postUploadResults: retJson.data[0].postUploadResults,
						source: retJson.data[0].originalFileName,
						destination: retJson.data[0].storagePath,
						mime: retJson.data[0].mime,
						uploadId,
					},
				];
			}
			return null;
		} catch (err) {
			onProgress(-1);
		}
		return null;
	}

	static async uploadOneFile(
		file: File,
		uploadId: string,
		fileType: EstimaticsFileTypes,
		store: EstimaticsProjectStoreType,
		postUploadCommand: string
	): Promise<
		| {
			source: string;
			destination: string;
			uploadId: string;
			mime: string;
			postUploadResults: any[];
		}[]
		| null
	> {
		store.addFile({
			uploadId,
			originalname: file.name,
			uploadedSofar: 0,
			size: file.size,
			fileType,
			uploadDate: new Date().toLocaleDateString("en-US"),
			uploadBy: getUserStore().user!.lassoUserId,
		});
		const response = RestAPI.uploadFileWithProgress(
			file,
			uploadId,
			`organizations/${getUserStore().user!.organizationId}/private/estimates/${store.currentEstimateID}/files`,
			{
				estimateId: store.currentEstimateID,
				tpa: store.estimaticsData.tpa,
				carrier: store.estimaticsData.insuranceCarrier,
			},

			postUploadCommand,
			(progress) => {
				store.updateEstimaticsDataFileProgress(uploadId, progress);
				// logDebug(`Upload progress: ${progress.toFixed(2)}%`);
				// Here you can update the UI with the progress
			}
		);
		return response;
	}
	static async uploadFiles(
		files: FileList | File[] | null,
		fileType: EstimaticsFileTypes,
		store: EstimaticsProjectStoreType,
		postUploadCommand: string
	) {
		if (files?.length) {
			store.nextDisabled = store.nextDisabled + 1;
			try {
				const uploadIds: string[] = [];
				const prms: Promise<
					{ postUploadResults: any[]; source: string; destination: string; uploadId: string; mime: string }[] | null
				>[] = [];
				for (let i = 0; i < files.length; i++) {
					const file = files[i];
					const uploadId = uuidv4();
					uploadIds.push(uploadId);
					const response = RestAPI.uploadOneFile(file, uploadId, fileType, store, postUploadCommand);
					prms.push(response);
				}
				const dests = await Promise.allSettled(prms);
				dests.forEach((d, index) => {
					if (d.status === "fulfilled") {
						if (d.value![0].postUploadResults) {
							store.updatePostUploadResults(d.value![0]);
						}
						store.updateEstimaticsDataFileProperties(uploadIds[index], {
							remotePath: d.value![0].destination,
							mime: d.value![0].mime,
						});
					} else {
						store.updateEstimaticsDataFileProperties(uploadIds[index], { error: d.reason || "unknow error" });
						logError("Upload error", d.reason);
					}
				});
			} catch (error) {
				logError("Upload error", error);
			}
			store.nextDisabled = store.nextDisabled - 1;
		}
	}
}
